import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Search, SquareTerminal, Trash2 } from "lucide-react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  Tab,
  Tabs,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CopyToClipboard from "../../lib/common/components/CopyToClipboard";
import {
  deletePrompt,
  getGlobalPromptsList,
  getPromptsList,
} from "../../lib/redux/actions";
import toast from "../../lib/utils/toast";
import PropTypes from "prop-types";


// PROMPTS constants
const PROMPTS = {
  MARKETING: 1,
  IMAGE_GENERATION: 2,
  SALES: 3,
  MARKET_ANALYSIS: 4,
  GENERAL: 5
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const PromptLibraryDialog = ({ isOpen, onClose }) => {
  const [value, setValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [myPromptTypeFilter, setMyPromptTypeFilter] = useState("All type");
  const [globalPromptTypeFilter, setGlobalPromptTypeFilter] = useState("All type");
  const [loading,setLoading] = useState(false)
  const [myPromptsData, setMyPromptsData] = useState([]);
  const [globalPromptsData, setGlobalPromptsData] = useState([]);
  const [myPage, setMyPage] = useState(1);
  const [myTotal, setMyTotal] = useState(0);
  const [globalPage, setGlobalPage] = useState(1);
  const [globalTotal, setGlobalTotal] = useState(0);
  const LIMIT = 10;

  useEffect(() => {
    openPromptLibraryDialogFns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, searchQuery, myPromptTypeFilter, globalPromptTypeFilter]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setMyPromptTypeFilter("")
    setSearchQuery(""); // Reset search when switching tabs
    // reset pagination without refactor
    setMyPage(1);
    setGlobalPage(1);
    setMyPromptsData([]);
    setGlobalPromptsData([]);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // const handleMyPromptTypeChange = (event) => {
  //   setMyPromptTypeFilter(event.target.value);
  // };

  const handleGlobalPromptTypeChange = (event) => {
    setGlobalPromptTypeFilter(event.target.value);
  };

  function openPromptLibraryDialogFns() {
    setLoading(true)
    const params = { search: searchQuery };

    if (value === 0) {
      const effectiveFilter = myPromptTypeFilter;
      if (effectiveFilter && effectiveFilter !== "All type") {
        params.promptType = effectiveFilter;
      }
      params.page = 1;
      params.limit = LIMIT;
      getPromptsList(params)
        .then((response) => {
          const resp = response?.data;
          // Support both array payloads and { data, total }
          const list = Array.isArray(resp) ? resp : (resp?.data || []);
          const total = Array.isArray(resp) ? list.length : (resp?.total || 0);
          setMyPromptsData(list);
          setMyTotal(total);
          setMyPage(1);
        })
        .catch((error) => {
          toast.error("Error fetching prompt library data.");
        }).finally(() => {
          setLoading(false);
        });
    }
    if (value === 1) {
      const effectiveFilter = globalPromptTypeFilter;
      if (effectiveFilter && effectiveFilter !== "All type") {
        params.promptType = effectiveFilter;
      }
      params.page = 1;
      params.limit = LIMIT;
      getGlobalPromptsList(params)
        .then((response) => {
          const resp = response?.data;
          const list = Array.isArray(resp) ? resp : (resp?.data || []);
          const total = Array.isArray(resp) ? list.length : (resp?.total || 0);
          setGlobalPromptsData(list);
          setGlobalTotal(total);
          setGlobalPage(1);
        })
        .catch((error) => {
          toast.error("Error fetching global prompt library data.");
        }).finally(() => {
          setLoading(false);
        });
    }
  }

  // Minimal load more (no refactor to existing logic)
  const fetchMoreMyPrompts = () => {
    if (loading) return;
    const nextPage = myPage + 1;
    const params = { search: searchQuery, page: nextPage, limit: LIMIT };
    const effectiveFilter = myPromptTypeFilter;
    if (effectiveFilter && effectiveFilter !== "All type") {
      params.promptType = effectiveFilter;
    }
    getPromptsList(params)
      .then((response) => {
        const resp = response?.data?.data;
        const list = Array.isArray(resp) ? resp : (resp?.data || []);
        const total = Array.isArray(resp) ? (myTotal || list.length) : (resp?.total || myTotal);
        setMyPromptsData((prev) => [...prev, ...list]);
        setMyPage(nextPage);
        setMyTotal(total);
      })
      .catch(() => {
        toast.error("Error fetching more prompts.");
      });
  };

  const fetchMoreGlobalPrompts = () => {
    if (loading) return;
    const nextPage = globalPage + 1;
    const params = { search: searchQuery, page: nextPage, limit: LIMIT };
    const effectiveFilter = globalPromptTypeFilter;
    if (effectiveFilter && effectiveFilter !== "All type") {
      params.promptType = effectiveFilter;
    }
    getGlobalPromptsList(params)
      .then((response) => {
        const resp = response?.data?.data;
        const list = Array.isArray(resp) ? resp : (resp?.data || []);
        const total = Array.isArray(resp) ? (globalTotal || list.length) : (resp?.total || globalTotal);
        setGlobalPromptsData((prev) => [...prev, ...list]);
        setGlobalPage(nextPage);
        setGlobalTotal(total);
      })
      .catch(() => {
        toast.error("Error fetching more global prompts.");
      });
  };

  const handleDeletePrompt = (dynamicId) => {
    deletePrompt(dynamicId)
      .then(() => {
        toast.success("Prompt deleted successfully");
        // Refresh the prompts list
        openPromptLibraryDialogFns();
      })
      .catch((error) => {
        toast.error("Error deleting prompt");
      });
  };

  return (
    <Dialog onClose={onClose} open={isOpen} maxWidth="sm" fullWidth>
      <DialogTitle>Prompt Library</DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab label="My Prompts" {...a11yProps(0)} />
            <Tab label="Global" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
        <div className="flex flex-col sm:flex-row gap-0 sm:gap-4">
          <OutlinedInput
            startAdornment={<Search position="start" className="mr-2" />}
            type="text"
            placeholder="Search prompt"
            className="w-full mb-4"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {/* <FormControl  className="w-full sm:w-[45%] mb-4">
            <InputLabel id="prompt-type-filter-label" sx={{ top: 3 }}>Filter by Type</InputLabel>
            <Select
              labelId="prompt-type-filter-label"
              id="prompt-type-filter"
              value={myPromptTypeFilter}
              label="Filter by Type"
              onChange={handleMyPromptTypeChange}
            >
              <MenuItem value="All type">
                All Types
              </MenuItem>
              <MenuItem value={PROMPTS.MARKETING}>Marketing</MenuItem>
              <MenuItem value={PROMPTS.SALES}>Sales</MenuItem>
            </Select>
          </FormControl> */}
          </div>
          {!loading && myPromptsData.length === 0 && (
            <div className="flex flex-col min-h-[160px] items-center justify-center ">
              <SquareTerminal size={"40px"} className="mb-8 text-green-800" />
              <strong>Currently, there are no saved prompts.</strong>
              <br />
              <p>Prompts you save will be displayed here.</p>
            </div>
          )}

          <div id="my-prompts-scroll" className="space-y-4 max-h-[400px] mt-4 overflow-y-auto">
            {!loading && (
              <InfiniteScroll
                dataLength={myPromptsData.length}
                next={fetchMoreMyPrompts}
                hasMore={myPromptsData.length < myTotal}
                loader={<div className="py-2 text-center text-sm">Loading...</div>}
                scrollableTarget="my-prompts-scroll"
              >
                {myPromptsData.map((category, index) => (
                  <div key={category?._id} className="border mb-4 p-3 rounded-md">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">{category.title}</h3>
                      <div className="flex items-center gap-2">
                        <CopyToClipboard
                          message={category.description}
                          id={category?._id}
                        />
                        <IconButton
                          onClick={() => handleDeletePrompt(category._id)}
                          size="small"
                          className=" hover:text-red-700"
                        >
                          <Trash2 size={17} />
                        </IconButton>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      <strong>Prompt:</strong> {category.description}
                    </p>
                  </div>
                ))}
              </InfiniteScroll>
            )}
          </div>
          {loading && (
            <div className="h-[150px] flex items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-green-800 border-t-transparent"></div>
            </div>
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
        <div className="flex flex-col sm:flex-row gap-0 sm:gap-4">
          <OutlinedInput
            startAdornment={<Search position="start" />}
            type="text"
            placeholder="Search prompt"
            className="w-full mb-4"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <FormControl  className="w-full sm:w-[45%] mb-4">
            <InputLabel id="prompt-type-filter-label-global" sx={{ top: 3 }}>Filter by Type</InputLabel>
            <Select
              labelId="prompt-type-filter-label-global"
              id="prompt-type-filter-global"
              value={globalPromptTypeFilter}
              label="Filter by Type"
              onChange={handleGlobalPromptTypeChange}
            >
              <MenuItem value="All type">
                All Types
              </MenuItem>
              <MenuItem value={PROMPTS.MARKETING}>Marketing</MenuItem>
              <MenuItem value={PROMPTS.IMAGE_GENERATION}>Image Generation</MenuItem>
              <MenuItem value={PROMPTS.SALES}>Sales</MenuItem>
              <MenuItem value={PROMPTS.MARKET_ANALYSIS}>Market Analysis</MenuItem>
              <MenuItem value={PROMPTS.GENERAL}>General</MenuItem>
            </Select>
          </FormControl>
          </div>
          {!loading && globalPromptsData.length === 0 && (
            <div className="flex flex-col min-h-[160px] items-center justify-center ">
              <SquareTerminal size={"40px"} className="mb-8 text-green-800" />
              <strong>Currently, there are no global prompts.</strong>
            </div>
          )}

          <div id="global-prompts-scroll" className="space-y-4 max-h-[400px] mt-4 overflow-y-auto">
            {!loading && (
              <InfiniteScroll
                dataLength={globalPromptsData.length}
                next={fetchMoreGlobalPrompts}
                hasMore={globalPromptsData.length < globalTotal}
                loader={<div className="py-2 text-center text-sm">Loading...</div>}
                scrollableTarget="global-prompts-scroll"
              >
                {globalPromptsData.map((category, index) => (
                  <div key={category?._id} className="border mb-4 p-3 rounded-md">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">{category.title}</h3>
                      <CopyToClipboard
                        message={category.description}
                        id={category?._id}
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      <strong>Prompt:</strong> {category.description}
                    </p>
                  </div>
                ))}
              </InfiniteScroll>
            )}
          </div>
          {loading && (
            <div className="h-[150px] flex items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-green-800 border-t-transparent"></div>
            </div>
          )}
        
        </CustomTabPanel>
      </DialogContent>
    </Dialog>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node, // ✅ this line fixes the warning
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

PromptLibraryDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PromptLibraryDialog;
